import { useEffect, useMemo, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Star, BadgeCheck, MapPin, Clock } from "lucide-react";
import { api } from "../lib/api";
import ProProfileDialog from "./ProProfileDialog";
import LiveActivityTicker from "./LiveActivityTicker";

/**
 * "Top rated pros near you" — a public social-proof strip on the landing page.
 * Pulls curated featured pros from GET /api/pros/featured (no auth) and renders
 * a filterable, horizontally scrollable rail of clickable pro cards. Shows a
 * shimmer skeleton while loading; hidden only if the fetch fails or is empty.
 */
export default function TopProsStrip() {
    const [pros, setPros] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selected, setSelected] = useState(null);
    const [active, setActive] = useState("All");
    const [overflowing, setOverflowing] = useState(false);
    const railRef = useRef(null);

    useEffect(() => {
        let alive = true;
        api.get("/pros/featured")
            .then((r) => { if (alive) setPros(r.data?.pros || []); })
            .catch(() => { if (alive) setPros([]); })
            .finally(() => { if (alive) setLoading(false); });
        return () => { alive = false; };
    }, []);

    const categories = useMemo(() => {
        const seen = [];
        for (const p of pros) if (!seen.includes(p.specialty)) seen.push(p.specialty);
        return ["All", ...seen];
    }, [pros]);

    const visible = active === "All" ? pros : pros.filter((p) => p.specialty === active);

    // Reset the rail scroll to the start whenever the filter changes.
    useEffect(() => {
        if (railRef.current) railRef.current.scrollTo({ left: 0, behavior: "smooth" });
    }, [active]);

    // Track whether the rail actually overflows so the edge-fade only shows
    // when there is more to scroll (and never dangles under a short filtered list).
    useEffect(() => {
        const el = railRef.current;
        if (!el) return;
        const measure = () => setOverflowing(el.scrollWidth - el.clientWidth > 8);
        measure();
        window.addEventListener("resize", measure);
        return () => window.removeEventListener("resize", measure);
    }, [visible.length, loading]);

    if (!loading && !pros.length) return null;

    return (
        <section
            className="relative z-10 mx-auto max-w-7xl px-6 sm:px-10 pb-4 pt-6"
            data-testid="top-pros-strip"
        >
            <div className="flex items-end justify-between mb-8">
                <div>
                    <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-orange-500">
                        Verified &amp; ready
                    </span>
                    <h2 className="mt-2 font-display text-3xl sm:text-4xl font-extrabold uppercase tracking-tight">
                        Top rated <span className="text-orange-500">pros near you</span>
                    </h2>
                </div>
                <span className="hidden sm:flex items-center gap-1.5 font-mono text-xs text-zinc-500 dark:text-zinc-500">
                    <MapPin className="h-3.5 w-3.5 text-orange-500" /> Ontario
                </span>
            </div>

            {/* Live social-proof ticker */}
            <LiveActivityTicker />

            {/* Trade filter chips */}
            {!loading && categories.length > 2 && (
                <div className="mb-6 flex flex-wrap gap-2" data-testid="pros-filter">
                    {categories.map((cat) => {
                        const on = active === cat;
                        return (
                            <button
                                key={cat}
                                type="button"
                                onClick={() => setActive(cat)}
                                data-testid={`pros-filter-${cat.toLowerCase().replace(/\s+/g, "-")}`}
                                aria-pressed={on}
                                className={`rounded-full border px-3.5 py-1.5 text-xs font-semibold transition-[background-color,border-color,color] duration-300 ${
                                    on
                                        ? "border-orange-500/60 bg-orange-500/15 text-orange-600 dark:text-orange-300"
                                        : "border-white/10 text-zinc-500 dark:text-zinc-400 hover:border-white/30 hover:text-zinc-300"
                                }`}
                            >
                                {cat}
                            </button>
                        );
                    })}
                </div>
            )}

            <div className="relative">
                <div
                    ref={railRef}
                    className="flex gap-5 overflow-x-auto pb-4 -mx-6 px-6 sm:mx-0 sm:px-0 snap-x snap-mandatory scrollbar-hide"
                    data-testid="top-pros-rail"
                >
                {loading ? (
                    Array.from({ length: 6 }).map((_, i) => (
                        <div
                            key={`sk-${i}`}
                            className="shrink-0 w-[240px] rounded-3xl border border-white/10 bg-zinc-950/50 p-5"
                            data-testid="pro-card-skeleton"
                        >
                            <div className="flex items-center gap-3.5">
                                <div className="h-14 w-14 rounded-2xl bg-black/10 dark:bg-white/10 animate-pulse" />
                                <div className="flex-1 space-y-2">
                                    <div className="h-3.5 w-24 rounded bg-black/10 dark:bg-white/10 animate-pulse" />
                                    <div className="h-2.5 w-16 rounded bg-black/10 dark:bg-white/10 animate-pulse" />
                                </div>
                            </div>
                            <div className="mt-4 h-3 w-28 rounded bg-black/10 dark:bg-white/10 animate-pulse" />
                            <div className="mt-4 border-t border-white/5 pt-3 flex justify-between">
                                <div className="h-2.5 w-20 rounded bg-black/10 dark:bg-white/10 animate-pulse" />
                                <div className="h-2.5 w-10 rounded bg-black/10 dark:bg-white/10 animate-pulse" />
                            </div>
                        </div>
                    ))
                ) : visible.map((p, i) => (
                    <motion.article
                        key={p.id}
                        role="button"
                        tabIndex={0}
                        onClick={() => setSelected(p)}
                        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setSelected(p); } }}
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-60px" }}
                        transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1], delay: i * 0.06 }}
                        className="group relative shrink-0 w-[240px] snap-start cursor-pointer rounded-3xl border border-white/10 bg-zinc-950/50 p-5 transition-[border-color,transform] duration-400 hover:-translate-y-1 hover:border-orange-500/50 focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-500/60"
                        data-testid={`pro-card-${p.id}`}
                    >
                        <div className="flex items-center gap-3.5">
                            <div className="relative">
                                <img
                                    src={p.avatar_url}
                                    alt={p.name}
                                    loading="lazy"
                                    className="h-14 w-14 rounded-2xl object-cover ring-1 ring-white/10"
                                    draggable={false}
                                />
                                {p.verified && (
                                    <span className="absolute -bottom-1.5 -right-1.5 grid h-6 w-6 place-items-center rounded-full bg-force-bg">
                                        <BadgeCheck className="h-5 w-5 text-orange-500 fill-orange-500/20" />
                                    </span>
                                )}
                            </div>
                            <div className="min-w-0">
                                <p className="font-display text-base font-bold leading-tight truncate">{p.name}</p>
                                <p className="text-xs text-orange-500 font-semibold uppercase tracking-wide">{p.specialty}</p>
                            </div>
                        </div>

                        <div className="mt-4 flex items-center gap-1.5">
                            <Star className="h-4 w-4 text-orange-400 fill-orange-400" />
                            <span className="font-bold text-sm">{Number(p.rating).toFixed(1)}</span>
                            <span className="text-xs text-zinc-400 dark:text-zinc-500">
                                ({p.rating_count} reviews)
                            </span>
                        </div>

                        {p.available_this_week && (
                            <span
                                className="mt-3 inline-flex items-center gap-1.5 rounded-full border border-emerald-500/30 bg-emerald-500/15 px-2.5 py-0.5 text-[10px] font-semibold text-emerald-600 dark:text-emerald-300"
                                data-testid="pro-availability-badge"
                            >
                                <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse" />
                                Available this week
                            </span>
                        )}

                        <div className="mt-3 flex items-center justify-between border-t border-white/5 pt-3">
                            <span className="flex items-center gap-1 text-xs text-zinc-400 dark:text-zinc-500">
                                <MapPin className="h-3.5 w-3.5" /> {p.city}
                            </span>
                            <span className="text-xs font-semibold text-zinc-300 dark:text-zinc-300">
                                {p.jobs_completed} jobs
                            </span>
                        </div>
                    </motion.article>
                ))}
                </div>
                {/* Right-edge fade hints there are more pros to scroll (desktop).
                    Uses the themed --force-bg var so it fades to the correct
                    background in both light and dark, and only when overflowing. */}
                {overflowing && (
                    <div
                        className="pointer-events-none absolute inset-y-0 right-0 hidden sm:block w-20"
                        style={{ background: "linear-gradient(to left, var(--force-bg), transparent)" }}
                        aria-hidden="true"
                    />
                )}
            </div>

            <ProProfileDialog pro={selected} open={!!selected} onClose={() => setSelected(null)} />
        </section>
    );
}
